import { UserDisplayName, UserOptionLabel } from "@/components";
import { useDynamicFormContext } from "@/components/DynamicForm/DynamicFormProvider";
import { getUserIdForProjectLeaderRegion } from "@/components/DynamicForm/helper";
import { DEFAULT_DEBOUNCE_TIME } from "@/config";
import { FieldNames, FieldType } from "@/enums";
import { apiEndpoints } from "@/helpers";
import { useUserContext } from "@/providers";
import {
  useGetDynamicAutoCompleteQuery,
  useGetProjectQuery,
  useGetUsersByIdsMutation,
} from "@/services";
import { Form, Input, Select, Spin, Tag } from "antd";
import debounce from "lodash.debounce";
import { bool, object } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { DynamicFormItem } from "./DynamicFormItem";
import { getPlaceholderText } from "./helper/getPlaceholderText";
import {
  useCheckEditableFormField,
  useDefaultDepartmentDocument,
} from "./hooks";
import * as S from "./styled";

const { Option } = Select;

const DynamicAutoComplete = ({
  formField,
  formField: {
    property: { fieldType },
  },
  formDisabled,
}) => {
  const intl = useIntl();
  const { projectId } = useParams();
  const { flatForms } = useDynamicFormContext();
  const { userToken } = useUserContext();
  const { isDisabled, formRef, calculatedFields } =
    useCheckEditableFormField(formField);

  const [url, setUrl] = useState();
  const [options, setOptions] = useState();
  const [optionsHtml, setOptionsHtml] = useState();
  const [value, setValue] = useState();
  const { data: project, isFetching: isProjectFetching } = useGetProjectQuery(
    projectId,
    {
      skip: !projectId,
    }
  );
  const [selectedValueCostCenter, setSelectedValueCostCenter] = useState("");
  const [selectedTenant, setSelectedTenant] = useState();
  const [selectedTenantName, setSelectedTenantName] = useState();
  const [searchTerm, setSearchTerm] = useState();
  const [notFoundContent, setNotFoundContent] = useState(null);
  const [hasSetDefault, setHasSetDefault] = useState(false);
  const [isInitialOptions, setIsInitialOptions] = useState(true);
  const [isOnClear, setIsOnClear] = useState(false);
  const [userIdForDefaultDepartment, setUserIdForDefaultDepartment] =
    useState();
  const [loadingInitialValueOption, setLoadingInitialValueOption] = useState(
    () => {
      const fieldValue = formRef.current.getFieldValue(formField.property.name);

      return Array.isArray(fieldValue) ? !!fieldValue.length : !!fieldValue;
    }
  );
  const costCenterTenantId =
    formRef.current.getFieldValue("costCenterTenantId");
  const defaultDepartmentDocument = useDefaultDepartmentDocument(
    userIdForDefaultDepartment
  );

  const {
    data: autoCompleteValue,
    isFetching: fetchingAutoCompleteValue,
    isLoading: loadingAutoCompleteValue,
    isUninitialized: isAutoCompleteQueryUninitialized,
  } = useGetDynamicAutoCompleteQuery(url, {
    skip: !url,
  });
  const [
    getUsersByIds,
    {
      data: usersByIds,
      isFetching: fetchingUsers,
      isLoading: loadingUsers,
      isUninitialized: isGetUsersByIdsUninitialized,
    },
  ] = useGetUsersByIdsMutation();

  const isFetching = fetchingAutoCompleteValue || fetchingUsers;
  const isLoading = loadingAutoCompleteValue || loadingUsers;
  const isUninitialized =
    isGetUsersByIdsUninitialized && isAutoCompleteQueryUninitialized;

  useEffect(() => {
    if (!loadingInitialValueOption) return;

    setLoadingInitialValueOption(isLoading || isUninitialized);
  }, [isLoading, isUninitialized, loadingInitialValueOption]);

  useEffect(() => {
    setOptions(autoCompleteValue);
  }, [autoCompleteValue]);

  useEffect(() => {
    if (
      value === undefined &&
      defaultDepartmentDocument &&
      formField.property.url === apiEndpoints.departments.suggestions &&
      isOnClear
    ) {
      setSelectedTenant(defaultDepartmentDocument.tenantId);
      setSelectedTenantName(defaultDepartmentDocument.tenantName);
      setSelectedValueCostCenter(defaultDepartmentDocument.costCenter);
      var defaultDoc = {
        document: {
          name: defaultDepartmentDocument.department,
          department: defaultDepartmentDocument.department,
          costCenter: defaultDepartmentDocument.costCenter,
          tenantId: defaultDepartmentDocument.tenantId,
        },
      };
      formRef.current.setFieldsValue(defaultDoc.document);
      setValue(defaultDoc.document.department);
      setOptions([defaultDoc]);
      setIsOnClear(false);
    }
  }, [
    defaultDepartmentDocument,
    formField.property.url,
    formRef,
    value,
    isOnClear,
  ]);

  useEffect(() => {
    const regionalProjectLeaders = formRef.current.getFieldValue(
      FieldNames.ProjectLeaders
    );

    const userId =
      getUserIdForProjectLeaderRegion(regionalProjectLeaders, flatForms) ||
      userToken.user.id;

    setUserIdForDefaultDepartment(userId);
  }, [flatForms, formRef, userToken.user.id]);

  useEffect(() => {
    if (
      !isInitialOptions ||
      !defaultDepartmentDocument ||
      formField.property.url !== apiEndpoints.departments.suggestions
    ) {
      return;
    }
    const applyDepartmentDocument = (doc) => {
      if (!doc) return;
      setSelectedValueCostCenter(doc.costCenter);
      formRef.current?.setFieldsValue(doc);
      setValue(doc.department);
      setSelectedTenant(doc.tenantId);
      setSelectedTenantName(doc.tenantName);
    };

    const applyDefault = () => {
      applyDepartmentDocument(defaultDepartmentDocument);
      setOptions([{ defaultDepartmentDocument }]);
    };

    if (projectId && (isProjectFetching || !project || !options?.length)) {
      return;
    }

    if (projectId && project && options?.length) {
      if (project.costCenterTenantId) {
        const dept = options.find(
          ({ document }) =>
            document.tenantId === project.costCenterTenantId &&
            document.name === project.department
        );

        if (dept?.document?.costCenter) {
          applyDepartmentDocument(dept.document);
          setOptions([{ defaultDepartmentDocument: dept.document }]);
        } else {
          applyDefault();
        }
      } else {
        const first = options[0]?.document;
        if (first?.costCenter) {
          applyDepartmentDocument(first);
          setOptions([{ defaultDepartmentDocument }]);
        } else {
          applyDefault();
        }
      }
    } else {
      applyDefault();
    }

    setIsInitialOptions(false);
  }, [
    options,
    isInitialOptions,
    formField.property.url,
    projectId,
    project,
    isProjectFetching,
    defaultDepartmentDocument,
    formRef,
  ]);

  useEffect(() => {
    if (!usersByIds) return;

    setOptionsHtml(
      usersByIds.map(
        ({
          id,
          displayName,
          department,
          email,
          jobTitle,
          azureAdAccountEnabled,
        }) => (
          <Option
            key={id}
            label={
              <UserDisplayName
                displayName={displayName}
                userAccountEnabled={azureAdAccountEnabled}
              />
            }
          >
            <UserOptionLabel
              displayName={displayName}
              department={department}
              email={email}
              jobTitle={jobTitle}
            />
          </Option>
        )
      )
    );
  }, [usersByIds]);

  useEffect(() => {
    if (!formField || !formRef || value) return;

    const formValue = formRef.current.getFieldValue(formField.property.name);
    const fetchMultiSelectValues =
      fieldType === FieldType.MultiSelectAutoComplete &&
      formValue?.some((value) => !options?.includes(value));
    const fetchSingleSelectValue =
      fieldType !== FieldType.MultiSelectAutoComplete &&
      formValue &&
      !options?.some(({ value }) => value === formValue);

    if (fetchMultiSelectValues || fetchSingleSelectValue) {
      switch (formField.property.url) {
        case apiEndpoints.users.suggestions: {
          const ids = fetchSingleSelectValue ? [formValue] : formValue;
          getUsersByIds(ids);
          break;
        }
        case apiEndpoints.departments.suggestions:
        default:
          setUrl(
            `${formField.property.url}?query=${encodeURIComponent(
              formValue
            )}&ignoreFilter=true`
          );
          break;
      }
    }

    setValue(formValue);

    if (
      hasSetDefault ||
      formField.property.url !== apiEndpoints.departments.suggestions ||
      !flatForms
    )
      return;
    setHasSetDefault(true);
  }, [
    fieldType,
    flatForms,
    formField,
    formRef,
    getUsersByIds,
    hasSetDefault,
    options,
    costCenterTenantId,
    value,
  ]);

  useEffect(() => {
    if (
      formField &&
      formRef &&
      formField.property.url === apiEndpoints.departments.suggestions
    ) {
      formRef.current.setFieldsValue({
        [formField.name]: value,
        costCenterTenantId: selectedTenant || "",
      });
    }
  }, [formField, formRef, value, selectedTenant]);

  useEffect(() => {
    if (
      calculatedFields &&
      Object.keys(calculatedFields).includes(formField.name)
    ) {
      setOptions([calculatedFields[formField.name]]);
      setValue(calculatedFields[formField.name].value);
    }
  }, [calculatedFields, formField.name]);

  useEffect(() => {
    if (options?.length) return;

    setValue(options?.displayName || options?.name);
  }, [options?.displayName, options?.name, options?.length]);

  useEffect(() => {
    if (!options?.length) return;

    const suggestions = options.filter(
      ({ document }) => document !== undefined
    );

    setOptionsHtml(
      suggestions.map((suggestion, index) => {
        const {
          document: {
            id,
            name,
            displayName,
            department,
            costCenter,
            tenantName,
            email,
            jobTitle,
            tenantId,
          },
        } = suggestion;
        switch (formField.property.url) {
          case apiEndpoints.users.suggestions:
            return (
              <Option key={id} label={displayName}>
                <UserOptionLabel
                  displayName={displayName}
                  department={department}
                  email={email}
                  jobTitle={jobTitle}
                />
              </Option>
            );
          case apiEndpoints.departments.suggestions: {
            return (
              <Option
                key={index}
                value={name + " - TenantID: " + tenantId}
                label={name}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html:
                      name +
                      (costCenter
                        ? `<br />${intl.formatMessage({
                            id: "general.costCenter",
                          })}: ${costCenter}
                          <br />${intl.formatMessage({
                            id: "general.tenant",
                          })}: ${tenantName}`
                        : ""),
                  }}
                />
              </Option>
            );
          }
          default:
            return (
              <Option key={name} value={name} label={name}>
                <span
                  dangerouslySetInnerHTML={{
                    __html: name,
                  }}
                />
              </Option>
            );
        }
      })
    );
  }, [formField.property.url, intl, options]);

  useEffect(() => {
    const getNotFoundContent = () => {
      if (isFetching) return <Spin size="small" />;

      if (searchTerm)
        return intl.formatMessage({ id: "general.noResultsFound" });

      return null;
    };

    setNotFoundContent(getNotFoundContent());
  }, [intl, isFetching, searchTerm]);

  const onChange = (val) => {
    if (val && val.includes(" - TenantID: ")) {
      const newTenantId = val.split(" - TenantID: ")[1];
      setSelectedTenant(newTenantId);
      var dept = val.split(" - TenantID: ")[0];
      setValue(dept);

      const suggestions = options.filter(
        ({ document }) =>
          document !== undefined &&
          document.tenantId === newTenantId &&
          document.name === dept
      );
      const chosen = suggestions[0]?.document;
      if (chosen) {
        setSelectedValueCostCenter(chosen.costCenter);
        setSelectedTenantName(chosen.tenantName);
      }
    } else {
      setValue(val);
    }
  };

  const onSearch = debounce((value) => {
    setSearchTerm(value);
    if (!value) {
      setOptions();
      return;
    }

    setUrl(`${formField.property.url}?query=${value}`);
  }, DEFAULT_DEBOUNCE_TIME);

  return (
    <>
      <DynamicFormItem formFieldProperty={formField.property}>
        {loadingInitialValueOption ? (
          <Spin size="small" />
        ) : (
          <S.DynamicSelect
            width="80%"
            getPopupContainer={(trigger) => trigger.parentNode}
            data-test-id={`dynamic-auto-complete-select-${formField.property.name}`}
            mode={
              fieldType === FieldType.MultiSelectAutoComplete
                ? "multiple"
                : undefined
            }
            showSearch
            allowClear
            defaultActiveFirstOption={false}
            placeholder={getPlaceholderText(formDisabled, formField, intl)}
            optionLabelProp="label"
            filterOption={false}
            disabled={formDisabled || isDisabled}
            onSearch={onSearch}
            onChange={onChange}
            notFoundContent={notFoundContent}
            loading={isFetching}
            onClear={() => setIsOnClear(true)}
          >
            {optionsHtml}
          </S.DynamicSelect>
        )}
      </DynamicFormItem>
      {!!selectedValueCostCenter && (
        <>
          <Tag>
            {intl.formatMessage({ id: "general.costCenter" })}:
            {selectedValueCostCenter}
            <br />
            {intl.formatMessage({ id: "general.tenant" })}:{selectedTenantName}
          </Tag>
          <Form.Item hidden={true} name="costCenterTenantId" id="costCenterTenantId">
            <Input />
          </Form.Item>
        </>
      )}
    </>
  );
};

DynamicAutoComplete.propTypes = {
  formField: object.isRequired,
  formDisabled: bool,
};

export { DynamicAutoComplete };
